import React from 'react';
import './ProtobufObjectTable.css';

const ProtobufObjectTable = ({
  object,
  typeName,
  protobufRoot,
  editable,
  onChange,
  readOnlyFields = [],
  recursive = false,
  numberTableColumns = 3,
  defaultIncrement = 1,
  modifyNumberArrays = false,
  modifyComplexArrays = true
}) => {
  if (!object || !protobufRoot) {
    return <div>Loading...</div>;
  }

  const MessageType = protobufRoot.lookupType(typeName);

  const getDefaultValue = (field) => {
    if(field.resolvedType){
      if(field.resolvedType.values){
        return field.resolvedType.values[0];
      }
      return field.resolvedType.create();
    }
    switch(field.type){
      case 'bool':
        return false;
      case 'int32':
      case 'uint32':
      case 'float':
      case 'double':  
        return 0;
      case 'string':
        return '';
      case 'enum':
        return 0;
/*      case 'message':
        console.log("getDefaultValue for message", field.resolvedType.fieldsArray);
        return getDefaultValues(field.resolvedType.fieldsArray);
        */
      default:
        return null;
    }
  }

  const getDefaultValues = (fields) => {
    const defaultValues = {};
    fields.forEach((field) => {
      defaultValues[field.name] = getDefaultValue(field);
    });
    return defaultValues;
  };

  const defaultValues = getDefaultValues(MessageType.fieldsArray);
  const renderedObject = { ...defaultValues, ...object };

  const formatNumber = (number) => {
    return number.toFixed(3).replace(/\.?0+$/, '');
  };

  const handleDelete = (key, index) => {
    const updatedArray = [...object[key]];
    updatedArray.splice(index, 1);
    handleChange(key, updatedArray);
  };

  const handleClone = (key, index) => {
    const updatedArray = [...object[key]];
    const clonedElement = JSON.parse(JSON.stringify(updatedArray[index])); // Deep clone the element
    updatedArray.splice(index, 0, clonedElement);
    handleChange(key, updatedArray);
  };

  const handleAdd = (key) => {
    const updatedArray = [...object[key]];
    const field = MessageType.fields[key];
    let newElement;

      newElement = getDefaultValue(field);
    console.log("handleAdd", key, newElement);

    updatedArray.push(newElement);
    handleChange(key, updatedArray);
  };

  const handleMoveUp = (key, index) => {
    if (index <= 0) return; // Can't move up if it's the first element
    const updatedArray = [...object[key]];
    [updatedArray[index - 1], updatedArray[index]] = [updatedArray[index], updatedArray[index - 1]];
    handleChange(key, updatedArray);
  };

  const handleMoveDown = (key, index) => {
    if (index >= object[key].length - 1) return; // Can't move down if it's the last element
    const updatedArray = [...object[key]];
    [updatedArray[index + 1], updatedArray[index]] = [updatedArray[index], updatedArray[index + 1]];
    handleChange(key, updatedArray);
  };

  const renderEmojiButton = (icon, onClick, disabled = false) => (
    <span
      role="button"
      className={`button ${disabled ? 'disabled' : ''}`}
      onClick={disabled ? null : onClick}
    >
      {icon}
    </span>
  );

  const renderArrayAsTable = (key, array) => {
    let columns = numberTableColumns;
    if (editable && (numberTableColumns <= 0 || numberTableColumns == null)) {
      columns = 1; // Force to 1 column if editable and columns are not set correctly
    }

    if (columns <= 0 || columns == null) {
      return array.join(', ');
    }

    const rows = [];
    for (let i = 0; i < array.length; i += columns) {
      rows.push(array.slice(i, i + columns));
    }

    return (
      <div>
        <table className="array-table">
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((value, colIndex) => (
                  <td key={colIndex}>
                    {typeof value === 'number' ? (
                      editable ? (
                        <input
                          type="number"
                          value={value}
                          step={defaultIncrement}
                          onChange={(e) => handleArrayChange(key, rowIndex * columns + colIndex, parseFloat(e.target.value))}
                        />
                      ) : (
                        formatNumber(value)
                      )
                    ) : (
                      value
                    )}
                    {editable && modifyNumberArrays && (
                      <>
                        {renderEmojiButton('👥', () => handleClone(key, rowIndex * columns + colIndex))}
                        {renderEmojiButton('❌', () => handleDelete(key, rowIndex * columns + colIndex))}
                        {renderEmojiButton('⬆️', () => handleMoveUp(key, rowIndex * columns + colIndex), rowIndex * columns + colIndex === 0)}
                        {renderEmojiButton('⬇️', () => handleMoveDown(key, rowIndex * columns + colIndex), rowIndex * columns + colIndex === array.length - 1)}
                      </>
                    )}
                  </td>
                ))}
                {row.length < columns &&
                  [...Array(columns - row.length)].map((_, colIndex) => <td key={`empty-${colIndex}`} />)}
              </tr>
            ))}
          </tbody>
        </table>
        {editable && modifyNumberArrays && (
          renderEmojiButton('➕', () => handleAdd(key))
        )}
      </div>
    );
  };

  const renderArray = (key, value) => {
    return (
      <div>
        {value.map((item, index) => (
          <div key={index} className="array-item">
            {renderValue(key, item, index)}
            {editable && modifyComplexArrays && (
              <>
                {renderEmojiButton('👥', () => handleClone(key, index))}
                {renderEmojiButton('❌', () => handleDelete(key, index))}
                {renderEmojiButton('⬆️', () => handleMoveUp(key, index), index === 0)}
                {renderEmojiButton('⬇️', () => handleMoveDown(key, index), index === value.length - 1)}
              </>
            )}
            <hr/>
          </div>
        ))}
        {editable && modifyComplexArrays && (
          renderEmojiButton('➕', () => handleAdd(key))
        )}
      </div>
    );
  };

  const renderValue = (key, value, index = -1) => {
    const field = MessageType.fields[key];
    let enumType = null;
    try {
      enumType = protobufRoot.lookupEnum(field.type);
    } catch (e) {}
    if (enumType) {
      return enumType.valuesById[value] || value;
    }
    if (Array.isArray(value) && typeof value[0] === 'number') {
      return renderArrayAsTable(key, value);
    }
    if (Array.isArray(value)) {
      return renderArray(key, value);
    }

    if (typeof value === 'number') {
      return formatNumber(value);
    }
    if (typeof value === 'boolean') {
      return JSON.stringify(value);
    }
    if (typeof value === 'string') {
      return value;
    }
    if (recursive && value) {
      const field = MessageType.fields[key];
      if (!field || !field.resolvedType) {
        console.warn("Can't find field protobuf type", key, "in", MessageType, "field", field);
        return JSON.stringify(value);
      }

      return (
        <ProtobufObjectTable
          object={value}
          typeName={field.resolvedType.name}
          protobufRoot={protobufRoot}
          editable={editable}
          onChange={(nestedKey, nestedValue) => {
            index >= 0
              ? handleArrayChange(key, index, { ...value, [nestedKey]: nestedValue })
              : handleChange(key, { ...value, [nestedKey]: nestedValue });
          }}
          recursive={true}
          numberTableColumns={numberTableColumns}
          defaultIncrement={defaultIncrement}
          modifyNumberArrays={modifyNumberArrays}
          modifyComplexArrays={modifyComplexArrays}
        />
      );
    }
    return JSON.stringify(value);
  };

  const handleChange = (key, value) => {
    if (onChange) {
      console.log("handleChange", key, value);
      onChange(key, value);
    }
  };

  const handleArrayChange = (key, index, value) => {
    console.log("handleArrayChange", key, index, value);
    const updatedArray = [...object[key]];
    updatedArray[index] = value;
    handleChange(key, updatedArray);
  };

  const renderInput = (key, value) => {
    const field = MessageType.fields[key];
    if (readOnlyFields.includes(key)) {
      return renderValue(key, value);
    }

    if (typeof value === 'object') {
      return renderValue(key, value);
    }

    if (field) {
      switch (field.type) {
        case 'bool':
          return (
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => handleChange(key, e.target.checked)}
            />
          );
        case 'int32':
        case 'uint64':
        case 'uint32':
        case 'float':
        case 'double':
          return (
            <input
              type="number"
              value={value}
              step={defaultIncrement}
              onChange={(e) => handleChange(key, parseFloat(e.target.value))}
            />
          );
        case 'string':
          if (field.repeated) {
            return (
              <textarea
                value={value.join(', ')}
                onChange={(e) => handleChange(key, e.target.value.split(',').map(Number))}
              />
            );
          }

          return (
            <input
              type="text"
              value={value}
              onChange={(e) => handleChange(key, e.target.value)}
            />
          );
        default:
          let enumType = null;
          try {
            enumType = protobufRoot.lookupEnum(field.type);
          } catch (e) {}
          if (enumType) {
            return (
              <select
                value={value}
                onChange={(e) => handleChange(key, parseInt(e.target.value, 10))}
              >
                {Object.entries(enumType.values).map(([enumKey, enumValue]) => (
                  <option key={enumValue} value={enumValue}>
                    {enumKey}
                  </option>
                ))}
              </select>
            );
          }
          return renderValue(key, value);
      }
    }
    return renderValue(key, value);
  };

  return (
    <div className="table-container">
      <table>
        <tbody>
          {Object.entries(renderedObject).map(([key, value], index) => (
            <tr key={key} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
              <td className="data-key">{key}</td>
              <td className="data-value">
                {editable ? renderInput(key, value) : renderValue(key, value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProtobufObjectTable;
